import React, { ReactNode, useEffect, useState } from 'react';
import Hotkeys from 'react-hot-keys';
import { motion } from 'framer-motion';
import { hideDialog } from '../../store/actions/prescriptionPad';
import { useDispatch, useSelector } from 'react-redux';
import {
    getAdvices,
    getDiagnosis,
    getExaminations,
    getFollowUp,
    getLabTests,
    getLabVitals,
    getMedications,
    getPrescriptionDyForm,
    getPatientMedicalHistory,
    getPrescriptionNotes,
    getRefer,
    getSymptoms,
    getVitals,
    getPrescription,
    getPrintConfigs,
    getTranslate,
    isPrescriptionEmpty,
    getDentalExaminations,
    getDentalProcedures,
    getElementsFromPrescription,
    getInjections,
    getCareCanvas,
} from '../../store/selectors/prescriptionPad';
import {
    OpIopElement,
    OpSpecElement,
    OpVisionElement,
    PX_OP_KEYS,
    Procedure,
    Tool,
} from '../../store/types/prescription';
import { GeniePadElementsSettingItem } from '../../api/pushGenie';
import {
    getGenieSetting,
    getPxElementConfig,
    getSectionNameConfig,
} from '../../store/selectors/session';
import PrescriptionPDF from './rightPanel/PrescriptionPDF';
import { Switch } from 'antd';
import ModalDialog from '../common/ModalDialog';
import { track } from '../../tracking';
import { GlobalState, GlobalStateV1 } from '../../store/reducers';
import {
    AdvicesComponent,
    CareCanvasComponent,
    DentalExaminationsComponent,
    DentalProceduresComponent,
    DiagnosisComponent,
    DynamicFormComponent,
    ExaminationsComponent,
    FollowUpComponent,
    InjectionTableComponent,
    LabTestsComponents,
    LabVitalsComponents,
    MedTableComponent,
    MedicalHistoryComponent,
    OpSummaryComponent,
    PPNotesComponent,
    ProceduresComponent,
    ReferComponent,
    SummaryEmptyView,
    SymptomsComponenent,
    VitalsComponent,
} from './PadComponents';
import ViewSummary from '../common/ViewSummary';
import { MHC } from '../../store/types/medicalHistory';
import { showOverrideVisitDialog } from '../common/overrideVisitDialog';
import { getSectionNameFromKey } from '../../pages/utils';
import { rxKeyToHeadingMap, rxKeyToIconMap } from '../../pages/opUtils';
import { getItemFromMisc, MISC_KEYS } from '../../store/idbHandler';
import { BizSettings } from '../../api/fetchMyGenie';
import CreatelocalPdf from './rightPanel/CreatelocalPdf';
import { getDefiniteMActiveId } from '../../store/localstorage';

export interface ToolWithMH extends Tool {
    medicalHistory: MHC;
}

export type PadElements = Partial<ToolWithMH>;

const PAD_COMPONENTS: {
    [key in keyof PadElements]: (sectionName?: string) => ReactNode;
} = {
    vitals: () => <Vitals />,
    medicalHistory: (sectionName?: string) => <MedicalHistory sectionName={sectionName} />,
    symptoms: (sectionName?: string) => <Symptoms sectionName={sectionName} />,
    diagnosis: (sectionName?: string) => <Diagnosis sectionName={sectionName} />,
    medications: (sectionName?: string) => <MedTable sectionName={sectionName} />,
    injections: (sectionName?: string) => <InjectionTable sectionName={sectionName} />,
    labTests: (sectionName?: string) => <LabTests sectionName={sectionName} />,
    labVitals: (sectionName?: string) => <LabVitals sectionName={sectionName} />,
    examinations: (sectionName?: string) => <Examinations sectionName={sectionName} />,
    prescriptionNotes: (sectionName?: string) => <PPNotes sectionName={sectionName} />,
    refer: (sectionName?: string) => <Refer sectionName={sectionName} />,
    advices: (sectionName?: string) => <Advices sectionName={sectionName} />,
    followup: (sectionName?: string) => <FollowUp sectionName={sectionName} />,
    dentalExaminations: () => <DentalExaminations />,
    dentalProcedures: () => <DentalProcedures />,
    opVision: () => <OpSumary rxKey={'opVision'} />,
    opIop: () => <OpSumary rxKey={'opIop'} />,
    opCurrentSpec: () => <OpSumary rxKey={'opCurrentSpec'} />,
    opSubjectiveRefraction: () => <OpSumary rxKey={'opSubjectiveRefraction'} />,
    opAutoRefraction: () => <OpSumary rxKey={'opAutoRefraction'} />,
    opFinalPrescription: () => <OpSumary rxKey={'opFinalPrescription'} />,
    procedures: () => <Procedures />,
    careCanvas: () => <CareCanvas />,
};

const SummaryView: React.FC = () => {
    const [viewType, setViewType] = useState<'text' | 'pdf'>('text');
    const [force, setForce] = useState(false);
    const medElements = useSelector((state) =>
        getGenieSetting(state as GlobalStateV1, 'medElements'),
    ) as GeniePadElementsSettingItem[];
    const padElementsConfig = useSelector(getPxElementConfig);
    const sectionNameConfig = useSelector(getSectionNameConfig);
    const dispatch = useDispatch();

    const closeMe = () => {
        dispatch(hideDialog('summary'));
    };

    const prescription = useSelector(getPrescription);
    const printConfigs = useSelector(getPrintConfigs);
    const translate = useSelector(getTranslate);
    const [localPdfEnabled, setLocalPdfEnabled] = useState<boolean>(false);
    const [isSettingsLoaded, setIsSettingsLoaded] = useState<boolean>(false);
    useEffect(() => {
        const fetchBizSettings = async () => {
            try {
                const bizSettings = await getItemFromMisc<BizSettings>(
                    getDefiniteMActiveId() || '',
                    MISC_KEYS.bizSettings,
                );
                setLocalPdfEnabled(!bizSettings.summaryPdfLocalDisabled);

                setIsSettingsLoaded(true);
            } catch (error) {
                console.error('Error fetching settings:', error);
                setIsSettingsLoaded(true); // to avoid infinite loading
            }
        };

        fetchBizSettings();
        return () => {
            setIsSettingsLoaded(false);
        };
    }, []);
    return (
        <ModalDialog>
            <Hotkeys keyName="esc" onKeyDown={closeMe}>
                <motion.div
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ zIndex: 20 }}
                    transition={{ duration: 0.2, ease: 'linear' }}
                    className="font-lato select-none z-20"
                >
                    <div onClick={closeMe} className="modal-overlay"></div>
                    <div className={`modal-content w-70p h-90p flex flex-col font-lato`}>
                        <div
                            style={{ userSelect: 'text' }}
                            className="flex flex-col flex-1 bg-neutral-9 rounded-4 overflow-hidden"
                        >
                            <div className="bg-white flex items-center p-16 justify-between rounded-t-4">
                                <p className="text-neutral-1 font-700">Rx Summary</p>
                                <div className="flex space-x-8 items-center">
                                    <span className="text-12">PDF View</span>
                                    <Switch
                                        className="link-color"
                                        checked={viewType === 'pdf'}
                                        size={'small'}
                                        onChange={() => {
                                            setViewType(viewType === 'pdf' ? 'text' : 'pdf');
                                            track('PREVIEW_TOGGLE', {
                                                viewType: viewType === 'pdf' ? 'text' : 'pdf',
                                            });
                                        }}
                                        style={{
                                            backgroundColor:
                                                viewType === 'pdf' ? '#1890ff' : 'rgba(0,0,0,.25)',
                                        }}
                                    ></Switch>
                                </div>
                                <div
                                    onClick={closeMe}
                                    className="flex items-center justify-center w-16 h-16 cursor-pointer"
                                >
                                    <svg
                                        className="w-16 h-16 fill-neutral-5"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 320 512"
                                    >
                                        <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
                                    </svg>
                                </div>
                            </div>
                            <div className="relative flex-1 overflow-hidden">
                                <div
                                    className={`bg-neutral-9 overflow-scroll absolute inset-0 ${
                                        viewType === 'text' ? 'z-1' : 'z-0'
                                    }`}
                                >
                                    <TextSummaryView />
                                </div>

                                <div
                                    className={`bg-neutral-9 overflow-scroll absolute inset-0 ${
                                        viewType === 'pdf' ? 'z-1' : 'z-0'
                                    }`}
                                >
                                    <div
                                        className={
                                            localPdfEnabled
                                                ? 'flex-1 h-full relative'
                                                : 'overflow-auto flex-1 h-full relative'
                                        }
                                    >
                                        {prescription &&
                                            printConfigs &&
                                            isSettingsLoaded &&
                                            (localPdfEnabled ? (
                                                <CreatelocalPdf
                                                    prescription={prescription}
                                                    printConfig={printConfigs}
                                                    translate={translate}
                                                    medElements={medElements}
                                                    padElementsConfig={padElementsConfig}
                                                    sectionNameConfig={sectionNameConfig}
                                                    force={force}
                                                />
                                            ) : (
                                                <PrescriptionPDF
                                                    prescription={prescription}
                                                    printConfig={printConfigs}
                                                    translate={translate}
                                                    medElements={medElements}
                                                    padElementsConfig={padElementsConfig}
                                                    sectionNameConfig={sectionNameConfig}
                                                    force={force}
                                                    onAlreadySavedError={() => {
                                                        showOverrideVisitDialog(
                                                            () => {
                                                                setForce(true);
                                                            },
                                                            () => {
                                                                setForce(false);
                                                                closeMe();
                                                            },
                                                        );
                                                    }}
                                                />
                                            ))}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center justify-between bg-white px-24 py-8 shadow-tight-5 mt-8">
                                <button onClick={closeMe} className="btn--small neutral-btn">
                                    Cancel
                                </button>
                                <button
                                    onClick={closeMe}
                                    className="px-12 py-8 btn--small primary-btn"
                                >
                                    Go To Prescription
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </Hotkeys>
        </ModalDialog>
    );
};

const TextSummaryView: React.FC = () => {
    const vitals = useSelector(getVitals);
    const symptoms = useSelector(getSymptoms);
    const diagonis = useSelector(getDiagnosis);
    const meds = useSelector(getMedications);
    const injections = useSelector(getInjections);
    const examinations = useSelector(getExaminations);
    const mh = useSelector(getPatientMedicalHistory);
    const followUp = useSelector(getFollowUp);
    const notes = useSelector(getPrescriptionNotes);
    const labtest = useSelector(getLabTests);
    const advices = useSelector(getAdvices);
    const refer = useSelector(getRefer);
    const df = useSelector(getPrescriptionDyForm);
    const labVitals = useSelector(getLabVitals);
    const dentalExaminations = useSelector(getDentalExaminations);
    const dentalProcedures = useSelector(getDentalProcedures);
    const opVision = useSelector(getElementsFromPrescription<OpVisionElement>('opVision'));
    const opIop = useSelector(getElementsFromPrescription<OpIopElement>('opIop'));
    const opCurrentSpec = useSelector(getElementsFromPrescription<OpSpecElement>('opCurrentSpec'));
    const opSubjectiveRefraction = useSelector(
        getElementsFromPrescription<OpSpecElement>('opSubjectiveRefraction'),
    );
    const opAutoRefraction = useSelector(
        getElementsFromPrescription<OpSpecElement>('opAutoRefraction'),
    );
    const opFinalPrescription = useSelector(
        getElementsFromPrescription<OpSpecElement>('opFinalPrescription'),
    );
    const procedures = useSelector(getElementsFromPrescription<Procedure>('procedures'));
    const careCanvas = useSelector(getCareCanvas);

    const prescriptionElements: PadElements = {
        vitals,
        symptoms,
        diagnosis: diagonis,
        medications: meds,
        examinations,
        medicalHistory: mh,
        followup: followUp,
        prescriptionNotes: notes,
        labTests: labtest,
        advices,
        refer,
        dyform: df,
        labVitals,
        dentalExaminations,
        dentalProcedures,
        opVision,
        opIop,
        opCurrentSpec,
        opSubjectiveRefraction,
        opAutoRefraction,
        opFinalPrescription,
        injections,
        procedures,
        careCanvas,
    };

    if (isPrescriptionEmpty(prescriptionElements)) {
        return <SummaryEmptyView />;
    }

    return <TextSummaryViewComponent />;
};

export const TextSummaryViewComponent: React.FC = () => {
    const padElementsConfig: GeniePadElementsSettingItem[] = useSelector(getPxElementConfig);
    const sectionNameConfig = useSelector(getSectionNameConfig);

    const remainingPxElements = Object.keys(PAD_COMPONENTS).filter((elem) => {
        if (!padElementsConfig?.find((e) => e.id === elem)) {
            return true;
        }
        return false;
    });

    const renderPadElements: (item: GeniePadElementsSettingItem) => React.ReactNode = (
        item: GeniePadElementsSettingItem,
    ) => {
        if (PAD_COMPONENTS[item.id as keyof PadElements]) {
            return PAD_COMPONENTS[item.id as keyof PadElements]?.(
                getSectionNameFromKey(item?.id, sectionNameConfig),
            );
        } else {
            return <DynamicForm configSectionId={item.id} />;
        }
    };
    const renderOtherElements = (item: string) =>
        PAD_COMPONENTS[item as keyof PadElements]?.(getSectionNameFromKey(item, sectionNameConfig));

    return (
        <ViewSummary
            padElementsConfig={padElementsConfig}
            remainingPxElements={remainingPxElements}
            renderOtherElements={renderOtherElements}
            renderPadElements={renderPadElements}
        />
    );
};

export default SummaryView;

function Symptoms({ sectionName }: { sectionName?: string }) {
    const symptoms = useSelector(getSymptoms);

    return <SymptomsComponenent symptoms={symptoms} sectionName={sectionName} />;
}

function Diagnosis({ sectionName }: { sectionName?: string }) {
    const diagonis = useSelector(getDiagnosis);

    return <DiagnosisComponent diagnosis={diagonis} sectionName={sectionName} />;
}

function MedTable({ sectionName }: { sectionName?: string }) {
    const meds = useSelector(getMedications);

    return <MedTableComponent medications={meds} sectionName={sectionName} />;
}

function InjectionTable({ sectionName }: { sectionName?: string }) {
    const injections = useSelector(getInjections);

    return <InjectionTableComponent injections={injections} sectionName={sectionName} />;
}

function Refer({ sectionName }: { sectionName?: string }) {
    const refer = useSelector(getRefer);
    return <ReferComponent refer={refer} sectionName={sectionName} />;
}

function FollowUp({ sectionName }: { sectionName?: string }) {
    const followUp = useSelector(getFollowUp);

    return <FollowUpComponent followup={followUp} sectionName={sectionName} />;
}

function MedicalHistory({ sectionName }: { sectionName?: string }) {
    const mh = useSelector(getPatientMedicalHistory);

    return <MedicalHistoryComponent medicalHistory={mh} sectionName={sectionName} />;
}

function DynamicForm({ configSectionId }: { configSectionId: string }) {
    const df = useSelector(getPrescriptionDyForm);

    const section = df.find((item) => item.id === configSectionId);
    return <DynamicFormComponent section={section} />;
}

function LabVitals({ sectionName }: { sectionName?: string }) {
    const labVitals = useSelector(getLabVitals);

    return <LabVitalsComponents labVitals={labVitals} sectionName={sectionName} />;
}

function LabTests({ sectionName }: { sectionName?: string }) {
    const labtest = useSelector(getLabTests);

    return <LabTestsComponents labTests={labtest} sectionName={sectionName} />;
}

function Vitals() {
    const vitals = useSelector(getVitals);

    return <VitalsComponent vitals={vitals} />;
}

function Examinations({ sectionName }: { sectionName?: string }) {
    const examinations = useSelector(getExaminations);

    return <ExaminationsComponent examinations={examinations} sectionName={sectionName} />;
}

function PPNotes({ sectionName }: { sectionName?: string }) {
    const notes = useSelector(getPrescriptionNotes);

    return <PPNotesComponent prescriptionNotes={notes} sectionName={sectionName} />;
}

function Advices({ sectionName }: { sectionName?: string }) {
    const advices = useSelector(getAdvices);

    return <AdvicesComponent advices={advices} sectionName={sectionName} />;
}

function DentalExaminations() {
    const dentalExaminations = useSelector(getDentalExaminations);

    return <DentalExaminationsComponent dentalExaminations={dentalExaminations} />;
}

function DentalProcedures() {
    const dentalProcedures = useSelector(getDentalProcedures);

    return <DentalProceduresComponent dentalProcedures={dentalProcedures} />;
}

function OpSumary({ rxKey }: { rxKey: PX_OP_KEYS }) {
    const elements = useSelector(
        getElementsFromPrescription<{ eye: { value: string }; [k: string]: { value: string } }>(
            rxKey,
        ),
    );

    return (
        <OpSummaryComponent
            elements={elements}
            heading={rxKeyToHeadingMap[rxKey]}
            icon={rxKeyToIconMap[rxKey]}
        />
    );
}

function Procedures() {
    const procedures = useSelector((state: GlobalState) =>
        getElementsFromPrescription<Procedure>('procedures')(state),
    );

    return <ProceduresComponent procedures={procedures} />;
}

function CareCanvas() {
    const careCanvas = useSelector(getCareCanvas);

    return <CareCanvasComponent careCanvas={careCanvas} />;
}
